import "./OverviewPage.scss";

import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import { CardBody } from "react-bootstrap";
import { useQueryClient } from "react-query";

import { BarChart } from "../../../components/charts/BarChart";
import { LineChart } from "../../../components/charts/LineChart";
import { StackedChart } from "../../../components/charts/StackedChart";
import type { IChartThemeMode } from "../../../components/charts/types";
import { useCharts } from "../../../components/charts/useCharts";
import { ChartUtils } from "../../../components/charts/utils";
import { useOrganizationContext } from "../../../contexts/organizations/organizationsContext";
import { useThemeMode } from "../../../contexts/theme-mode/ThemeModeProvider";
import type { Period } from "../../../hooks/reactQueryHooks/useAnalytic";
import { useAnalytics } from "../../../hooks/reactQueryHooks/useAnalytic";
import type { IApplication } from "../../../hooks/reactQueryHooks/useApplications";
import { useQueryParams } from "../../../hooks/useQueryParams";
import { getTimeInterval } from "../../../utils/getTimeInterval";
import { Badges } from "./Badges/Badges";
import { Filters } from "./Filters/Filters";
import { Notification } from "./Notification/Notification";

export interface IProps {
  applications?: IApplication[];
}
// production - August 1st, 2024
// dev && stage - June 1st, 2023
const TRACKING_DATE =
  process.env.REACT_APP_WEB_ENV === "production"
    ? 1722459600000
    : 1717189200000;

const OverviewPage: React.FC<IProps> = memo(({ applications }) => {
  const queryClient = useQueryClient();
  const { currentOrganization } = useOrganizationContext();
  const { mode } = useThemeMode();
  const [isDisabledSwitch, setIsDisabledSwitch] = useState(false);

  const [dateFilter, setDateFilter] = useState<{
    range: {
      fromDate: Date;
      toDate?: Date;
    };
    period: Period;
  } | null>(null);
  const [cacheRatioType, setCacheRatioType] = useState("Cumulative");

  const [isLoading, setIsLoading] = useState(true);

  const { queryValue: appQuery } = useQueryParams({
    queryStr: "projId",
  });

  const { queryValue: dateQuery, handleChange } = useQueryParams({
    queryStr: "date",
  });

  const analyticData = useAnalytics(currentOrganization?.id || "", {
    dateFilter: dateFilter?.range,
    applicationId: appQuery || undefined,
    period: dateFilter?.period,
  });

  useEffect(() => {
    if (dateFilter && applications?.length) {
      queryClient.removeQueries("analytics");
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      analyticData.refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFilter]);

  useEffect(() => {
    if (!analyticData.isLoading && !analyticData.isFetching) {
      setIsLoading(false);
    }
  }, [analyticData.isLoading, analyticData.isFetching]);

  const handleSwitchCacheRatioType = useCallback(
    (val: boolean) => {
      setCacheRatioType(val ? "Cumulative" : "Instant");
    },
    [setCacheRatioType],
  );

  const instantCacheHitRatioChartDataMemo = useMemo(() => {
    return {
      handler: handleSwitchCacheRatioType,
      value: cacheRatioType,
      default: "Cumulative",
      disabled: isDisabledSwitch,
    };
  }, [handleSwitchCacheRatioType, cacheRatioType, isDisabledSwitch]);

  useEffect(() => {
    setIsLoading(true);
    if (applications && !applications.length) {
      setIsLoading(false);
      return;
    }
    const filterData = dateQuery?.split(",");
    const dateType = filterData?.[0];
    setCacheRatioType("Cumulative");
    if (!dateType || dateType === "3M") {
      setIsDisabledSwitch(true);
    } else {
      setIsDisabledSwitch(false);
    }
    let currentApp = applications?.find(app => app.id === appQuery);

    let timeInterval = currentApp
      ? getTimeInterval(
          Number(currentApp.created) * 1000,
          TRACKING_DATE,
          dateType,
        )
      : getTimeInterval(
          Number(currentOrganization?.created) * 1000,
          TRACKING_DATE,
          dateType,
        );

    if (dateType !== "custom") {
      setDateFilter({
        range: {
          fromDate: timeInterval.fromDate,
        },
        period: timeInterval.period,
      });
      return;
    }
    if (dateType === "custom" && filterData?.length) {
      const fromDate = new Date(Number(filterData[1]));
      const toDate = new Date(Number(filterData[2]));
      if (
        isNaN(fromDate.getTime()) ||
        isNaN(toDate.getTime()) ||
        fromDate > toDate ||
        filterData.length !== 3
      ) {
        setTimeout(() => {
          handleChange("", true);
        });
        setDateFilter(null);
        return;
      }
      const period = ChartUtils.getTypePeriod({
        fromDate,
        toDate,
      });
      setDateFilter({
        range: {
          fromDate: new Date(
            new Date(fromDate.setUTCHours(0, 0, 0, 0)).setUTCDate(
              fromDate.getUTCDate() + 1,
            ),
          ),
          toDate: new Date(
            new Date(toDate.setUTCHours(23, 59, 59, 999)).setUTCDate(
              toDate.getUTCDate() + 1,
            ),
          ),
        },
        period: period as Period,
      });
    }
  }, [
    applications,
    appQuery,
    dateQuery,
    currentOrganization?.created,
    setCacheRatioType,
    handleChange,
  ]);

  const {
    cacheHitsChartData,
    requestsChartData,
    cacheHitRatioChartData,
    latencyChartData,
    tokensUsageChartData,
    costAndSavingsChartData,
    badgesData,
  } = useCharts({
    analyticData: analyticData.data,
    period: dateFilter?.period || "month",
    index: cacheRatioType === "Instant" ? 0 : 1,
  });

  return (
    <div className="card">
      <CardBody>
        <Notification
          trackingDate={TRACKING_DATE}
          currentOrganization={currentOrganization}
        />
        <Filters applications={applications || []} appQuery={appQuery} />
        <div className="">
          <Badges badgesData={badgesData} isLoading={isLoading} />
        </div>
        <div className="overview-charts">
          <div className="">
            <BarChart
              chartData={costAndSavingsChartData}
              themeMode={mode as IChartThemeMode}
              height={230}
              isLoading={isLoading}
            />
          </div>
          <div className="">
            <StackedChart
              chartData={tokensUsageChartData}
              themeMode={mode as IChartThemeMode}
              isLoading={isLoading}
              height={230}
            />
          </div>
          <div className="">
            <LineChart
              chartData={cacheHitRatioChartData}
              toggleData={instantCacheHitRatioChartDataMemo}
              themeMode={mode as IChartThemeMode}
              isLoading={isLoading}
              height={230}
            />
          </div>
          <div className="">
            <StackedChart
              chartData={cacheHitsChartData}
              themeMode={mode as IChartThemeMode}
              isLoading={isLoading}
              height={230}
            />
          </div>
          <div className="">
            <StackedChart
              chartData={requestsChartData}
              themeMode={mode as IChartThemeMode}
              isLoading={isLoading}
              height={230}
            />
          </div>
          <div className="">
            <LineChart
              chartData={latencyChartData}
              themeMode={mode as IChartThemeMode}
              isLoading={isLoading}
              height={230}
            />
          </div>
        </div>
      </CardBody>
    </div>
  );
});

export { OverviewPage };
